import { useActiveBundle } from '@/context/active-bundle';
import { useCurrentWorkspace } from '@/context/current-workspace';
import type { TargetTransition, TargetTransitionOptions } from '@/services/router/safe-navigate';
import type { MergedWorkspace } from '@/features/workspaces';
import { useFilterWorkspacesByName, useWorkspaceCount, useSampleWorkspace } from '@/features/workspaces';
import { AppBundle } from '@/utils/app-bundles';
import { navigateExternal } from '@/utils/navigate';
import { workspaceUrlFromCurrentPath } from '@/utils/navigation';
import {
  Avatar,
  Box,
  Button,
  Icon,
  Label,
  List,
  ListItem,
  PlainButton,
  PlainLink,
  PopoverContent,
  Search,
  Text,
  TextLabel,
  Tooltip,
  Truncated,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import { useAnalytics } from '@/services/analytics';
import { AnimatePresence, m, useReducedMotion } from 'motion/react';
import {
  FAST_02_STANDARD_PRODUCTIVE_TRANSITION,
  ENTRANCE_FADE_UP_ANIMATE,
  entranceFadeUpInitial,
  FAST_01_DURATION,
  FAST_02_ENTRANCE_PRODUCTIVE_TRANSITION,
  REDUCED_MOTION_TRANSITION,
} from '@/utils/motion';

import type { Ref } from 'react';
import { use, useMemo, useRef, useState } from 'react';
import { WorkspaceDot } from '../../../workspace-dot';
import { IconButton } from '../icon-button';
import * as styles from './index.css';
import { useMountEffect } from '@/hooks/state/use-mount-effect';
import { useAuth } from '@/services/auth';
import { useStore } from '@nanostores/react';
import {
  changeToSession,
  currentSessionStore,
  type Session,
  sessionsStore,
  setActiveAccount,
  updateSessions,
} from '@customerio/hydra-session-state';
import {
  MAX_ACCOUNTS,
  PENDING_SESSION_SWITCH_KEY,
  DECOUPLE_USERS_FLAG,
  AccountSwitcherContext,
  useAccountMemberships,
  switchAccount,
} from '@/features/account-switcher';
import { hydraUiApi, useHydraUIAPIMutation } from '@/services/api';
import { session as authSession } from '@/services/session';

const PANEL_SLIDE_DISTANCE = 160;

const panelVariants = {
  enter: (direction: 1 | -1) => ({
    x: direction > 0 ? PANEL_SLIDE_DISTANCE : -PANEL_SLIDE_DISTANCE,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: FAST_02_STANDARD_PRODUCTIVE_TRANSITION,
  },
  exit: (direction: 1 | -1) => ({
    x: direction > 0 ? -PANEL_SLIDE_DISTANCE : PANEL_SLIDE_DISTANCE,
    opacity: 0,
    transition: FAST_02_STANDARD_PRODUCTIVE_TRANSITION,
  }),
};

function itemStaggerTransition(index: number, shouldReduceMotion: boolean | null) {
  if (shouldReduceMotion === true) {
    return REDUCED_MOTION_TRANSITION;
  }
  return {
    ...FAST_02_ENTRANCE_PRODUCTIVE_TRANSITION,
    delay: Math.min(index, 5) * FAST_01_DURATION,
  };
}

function isWorkspaceActive(
  workspace: MergedWorkspace,
  currentWorkspace: MergedWorkspace | null,
  activeApp: AppBundle | null,
) {
  if (activeApp === AppBundle.Journeys) {
    return workspace.cio_workspace_id === currentWorkspace?.cio_workspace_id;
  }

  return workspace.id === currentWorkspace?.id;
}

function isSessionExpired(session: Session): boolean {
  return session.expires_at < Math.floor(Date.now() / 1000);
}

// Identity-provider display names for the login badge. Password/email logins are
// unlabeled in the design, and any provider not mapped here is title-cased.
const AUTH_PROVIDER_LABELS: Record<string, string> = {
  google: 'Google',
  saml: 'SAML',
  okta: 'Okta',
  microsoft: 'Microsoft',
};

function formatAuthProvider(provider: string | undefined): string | null {
  if (provider == null || isEmptyString(provider) || provider === 'password' || provider === 'email') {
    return null;
  }
  return AUTH_PROVIDER_LABELS[provider] ?? `${provider.charAt(0).toUpperCase()}${provider.slice(1)}`;
}

function SampleWorkspaceItem() {
  return (
    <Tooltip
      content="Explore Customer.io with sample data — view only, no changes allowed."
      placement="right"
      isCustom={true}
    >
      {(context) => (
        <PlainLink
          href="/sample-workspace"
          className={styles.workspaceItemLink}
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
        >
          <div className={styles.workspaceItemNameContainer}>
            <Icon name="a-b-test" size="sm" />
            <Truncated className={styles.workspaceItemName} aria-label="Sample Workspace">
              Sample Workspace
            </Truncated>
          </div>
        </PlainLink>
      )}
    </Tooltip>
  );
}

function useIsWorkspaceActive(workspace: MergedWorkspace) {
  const currentWorkspace = useCurrentWorkspace();
  const activeApp = useActiveBundle();
  return isWorkspaceActive(workspace, currentWorkspace, activeApp);
}

function WorkspaceDropdownWorkspace(props: {
  workspace: MergedWorkspace;
  onWorkspaceLinkClick: (event: React.MouseEvent<HTMLAnchorElement>, options?: TargetTransitionOptions) => void;
}) {
  const { workspace, onWorkspaceLinkClick } = props;
  const currentWorkspace = useCurrentWorkspace();
  const topLevelTransition: TargetTransition = {
    url: workspaceUrlFromCurrentPath(workspace.id, window.location.pathname),
    options: {
      shouldFadeOut: workspace.id !== currentWorkspace?.id,
      shouldUnrenderOutlet: false,
    },
  };

  return (
    <a
      href={topLevelTransition.url}
      className={styles.workspaceItemLink}
      onClick={(event) => {
        onWorkspaceLinkClick(event, topLevelTransition.options);
      }}
    >
      <div className={styles.workspaceItemNameContainer}>
        {!isEmptyString(workspace.color) ? <WorkspaceDot color={workspace.color} /> : null}
        <Truncated className={styles.workspaceItemName} aria-label={`${workspace.name} workspace`}>
          {workspace.name}
        </Truncated>
      </div>
    </a>
  );
}

function WorkspaceStaggerItem(props: {
  workspace: MergedWorkspace;
  index: number;
  shouldReduceMotion: boolean | null;
  hasToggled: boolean;
  isSampleWorkspace: boolean;
  onWorkspaceLinkClick: (event: React.MouseEvent<HTMLAnchorElement>, options?: TargetTransitionOptions) => void;
  currentWorkspaceRef: Ref<HTMLLIElement> | null;
}) {
  const isActive = useIsWorkspaceActive(props.workspace);

  return (
    <m.li
      className={`${styles.staggerItem} ${styles.workspaceItem}`}
      aria-current={isActive}
      ref={props.currentWorkspaceRef}
      initial={props.hasToggled ? entranceFadeUpInitial(props.shouldReduceMotion) : false}
      animate={ENTRANCE_FADE_UP_ANIMATE}
      transition={props.hasToggled ? itemStaggerTransition(props.index, props.shouldReduceMotion) : undefined}
    >
      {props.isSampleWorkspace ? (
        <SampleWorkspaceItem />
      ) : (
        <WorkspaceDropdownWorkspace workspace={props.workspace} onWorkspaceLinkClick={props.onWorkspaceLinkClick} />
      )}
    </m.li>
  );
}

type LoginGroupHeaderProps = {
  session: Session;
  isExpired: boolean;
  onLogout: (sessionId: number) => unknown;
  isLoggingOut: boolean;
};

// The login (email) that heads a group of accounts, with a log-out control on the
// right. Expired logins get a "Signed out" badge (their account rows link to
// re-auth) but keep the log-out control so a signed-out login can be removed from
// the switcher rather than occupying a session slot until re-auth.
function LoginGroupHeader({ session, isExpired, onLogout, isLoggingOut }: LoginGroupHeaderProps) {
  const providerLabel = formatAuthProvider(session.auth_provider);
  return (
    <Box
      className={styles.loginGroupHeader}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap="100"
      px="200"
      py="150"
    >
      <Box minW="0" display="flex" alignItems="center" gap="100">
        <Truncated text="product-p-active">{session.email}</Truncated>
        {isExpired ? (
          <Label color="grey" showStatusIndicator={true}>
            Signed out
          </Label>
        ) : providerLabel != null ? (
          <Label color="grey">{providerLabel}</Label>
        ) : null}
      </Box>
      <Box flexShrink="0">
        <IconButton
          icon="logout"
          label={`Log out of ${session.name} account`}
          tooltip="Log out"
          onClick={() => onLogout(session.id)}
          isDisabled={isLoggingOut}
        />
      </Box>
    </Box>
  );
}

type SwitchAccountButtonProps = { onClick: () => void; accountName?: string; accountLogoUrl?: string };

function SwitchAccountButton(props: SwitchAccountButtonProps) {
  const { isSwitchingAccounts } = use(AccountSwitcherContext);
  const currentSession = useStore(currentSessionStore);

  if (currentSession == null) {
    return null;
  }

  // In membership mode the active account can differ from the login's home
  // account, so prefer the resolved active-account name when one is supplied;
  // the email stays the login identity's email.
  const displayName = props.accountName ?? currentSession.name;

  return (
    <PlainButton
      onClick={props.onClick}
      display="flex"
      w="full"
      justifyContent="space-between"
      alignItems="center"
      textAlign="left"
      px="200"
      py="150"
      className={styles.accountSwitcherButton}
      aria-expanded={isSwitchingAccounts}
      aria-controls={isSwitchingAccounts ? 'account-switcher-accounts-list' : undefined}
      aria-label="Switch accounts"
    >
      <Box display="flex" alignItems="center" gap="150" minW="0">
        <Avatar
          size="s"
          name={displayName}
          src={isEmptyString(props.accountLogoUrl) ? undefined : props.accountLogoUrl}
          autoColor={true}
        />
        <Box minW="0">
          <Text display="block" text="product-p-active" truncate={true}>
            {displayName}
          </Text>
          <Text display="block" text="product-pxs-secondary" truncate={true}>
            {currentSession.email}
          </Text>
        </Box>
      </Box>
      <Icon className={styles.accountSwitcherButtonIcon} name="chevron-right" />
    </PlainButton>
  );
}

type AccountMembershipItem = NonNullable<ReturnType<typeof useAccountMemberships>['data']>[number];

// What selecting an account row does.
//  - `switch-account`: keep the current login, change only the active account
//    (X-Active-Account-Id, no re-auth).
//  - `switch-login`: change identity/session, landing on that login's home.
//  - `switch-login-account`: change to another login AND land on a specific one
//    of its accounts.
type AccountRowAction =
  | { type: 'switch-account'; accountId: number }
  | { type: 'switch-login'; sessionId: number }
  | { type: 'switch-login-account'; sessionId: number; accountId: number };

type AccountRowData = {
  key: string;
  accountName: string;
  logoUrl: string | undefined;
  isHome: boolean;
  isCurrent: boolean;
  action: AccountRowAction;
};

type LoginGroupData = {
  session: Session;
  isExpired: boolean;
  accounts: AccountRowData[];
};

type BuildAccountRowsParams = {
  session: Session;
  isCurrentLogin: boolean;
  currentLoginDecoupled: boolean;
  // The authenticated user's home account, used as a home-id fallback for the
  // current login when session metadata predates home_account_id.
  currentUserAccountId: number | undefined;
  memberships: AccountMembershipItem[];
  membershipSwitchingEnabled: boolean;
  activeAccountId: number;
};

// session.name is the login's home-account name; fall back to a neutral label so
// a row never renders blank if the name is missing.
function homeAccountName(session: Session): string {
  return isEmptyString(session.name) ? 'Your account' : session.name;
}

// Prepend the current login's home account when it isn't already in `rows`, so a
// user on a non-home account always has a way back. No-op when the home id is
// unknown (the collapsed fallback row still covers that via the home sentinel).
function withHomeRow(
  rows: AccountRowData[],
  session: Session,
  homeAccountId: number | undefined,
  activeAccountId: number,
): AccountRowData[] {
  if (homeAccountId == null || rows.some((row) => row.isHome)) {
    return rows;
  }
  return [
    {
      key: `home-${session.id}`,
      accountName: homeAccountName(session),
      logoUrl: undefined,
      isHome: true,
      isCurrent: homeAccountId === activeAccountId,
      action: { type: 'switch-account', accountId: homeAccountId },
    },
    ...rows,
  ];
}

// The account rows for a single login. Prefers the login's own accounts from
// session metadata (available for every login); falls back to the current
// login's /v1/memberships, then to just the login's home account — so the
// switcher still works against a backend that predates per-login accounts.
//
// The active login is only expanded into multiple switchable accounts when it is
// decoupled: the backend honors X-Active-Account-Id only when decouple_users is
// on, so without it switching would silently no-op — a backfilled login can carry
// several account_users rows while the flag is off. We can't read another login's
// flag client-side, so its accounts are best-effort (the backend validates on
// switch and lands on home if not permitted).
// Rows from a login's own accounts (session metadata). Accounts under the current
// login switch the active account; under another login they switch to that login
// and land on the chosen account.
function metadataAccountRows(
  session: Session,
  accounts: NonNullable<Session['accounts']>,
  homeAccountId: number | undefined,
  activeAccountId: number,
  isCurrentLogin: boolean,
): AccountRowData[] {
  return accounts.map((account) => ({
    key: `${session.id}-account-${account.account_id}`,
    accountName: account.account_name,
    logoUrl: account.logo_url,
    isHome: account.account_id === homeAccountId,
    isCurrent: isCurrentLogin && account.account_id === activeAccountId,
    action: isCurrentLogin
      ? { type: 'switch-account', accountId: account.account_id }
      : { type: 'switch-login-account', sessionId: session.id, accountId: account.account_id },
  }));
}

// Rows from the current login's /v1/memberships (fallback before session metadata
// carries per-login accounts).
function membershipAccountRows(
  memberships: AccountMembershipItem[],
  homeAccountId: number | undefined,
  activeAccountId: number,
): AccountRowData[] {
  return memberships.map((membership) => ({
    key: `account-${membership.account_id}`,
    accountName: membership.account_name,
    logoUrl: membership.logo_url,
    isHome: membership.account_id === homeAccountId,
    isCurrent: membership.account_id === activeAccountId,
    action: { type: 'switch-account', accountId: membership.account_id },
  }));
}

// The single home row when there's nothing to expand. For the current login,
// selecting it switches the active account back to home (switch-login would no-op
// on the already-active session); the home sentinel 0 resolves to home when the id
// is unknown. Other logins switch login.
function fallbackAccountRow(
  session: Session,
  homeAccountId: number | undefined,
  activeAccountId: number,
  isCurrentLogin: boolean,
): AccountRowData {
  return {
    key: `login-${session.id}`,
    accountName: homeAccountName(session),
    logoUrl: undefined,
    isHome: true,
    isCurrent: isCurrentLogin && (homeAccountId == null || homeAccountId === activeAccountId),
    action: isCurrentLogin
      ? { type: 'switch-account', accountId: homeAccountId ?? 0 }
      : { type: 'switch-login', sessionId: session.id },
  };
}

function buildAccountRows(params: BuildAccountRowsParams): AccountRowData[] {
  const {
    session,
    isCurrentLogin,
    currentLoginDecoupled,
    currentUserAccountId,
    memberships,
    membershipSwitchingEnabled,
    activeAccountId,
  } = params;

  // The login's home account id. For the current login, fall back to the
  // authenticated user's account when the session metadata predates home_account_id.
  const homeAccountId = isCurrentLogin ? (session.home_account_id ?? currentUserAccountId) : session.home_account_id;

  const metadataAccounts = session.accounts;
  const canExpandFromMetadata = metadataAccounts != null && metadataAccounts.length > 0;
  // Other logins list their accounts as-is; the current login only expands when
  // decoupled (else X-Active-Account-Id switching is a no-op).
  const expandFromMetadata = canExpandFromMetadata && (isCurrentLogin ? currentLoginDecoupled : true);

  if (expandFromMetadata && metadataAccounts != null) {
    const rows = metadataAccountRows(session, metadataAccounts, homeAccountId, activeAccountId, isCurrentLogin);
    // Guarantee a home row for the current login even if the metadata omits it.
    return isCurrentLogin ? withHomeRow(rows, session, homeAccountId, activeAccountId) : rows;
  }

  if (isCurrentLogin && membershipSwitchingEnabled) {
    const rows = membershipAccountRows(memberships, homeAccountId, activeAccountId);
    // Guarantee the home account is selectable even if the API omitted it.
    return withHomeRow(rows, session, homeAccountId, activeAccountId);
  }

  return [fallbackAccountRow(session, homeAccountId, activeAccountId, isCurrentLogin)];
}

/**
 * Groups accounts beneath the login that reaches them — every login lists the
 * accounts it can access.
 */
function buildLoginGroups(params: {
  sessions: Session[];
  currentSession: Session | null;
  currentLoginDecoupled: boolean;
  currentUserAccountId: number | undefined;
  memberships: AccountMembershipItem[];
  membershipSwitchingEnabled: boolean;
  activeAccountId: number;
}): LoginGroupData[] {
  const {
    sessions,
    currentSession,
    currentLoginDecoupled,
    currentUserAccountId,
    memberships,
    membershipSwitchingEnabled,
    activeAccountId,
  } = params;

  return sessions.map((session) => ({
    session,
    isExpired: isSessionExpired(session),
    accounts: buildAccountRows({
      session,
      isCurrentLogin: session.id === currentSession?.id,
      currentLoginDecoupled,
      currentUserAccountId,
      memberships,
      membershipSwitchingEnabled,
      activeAccountId,
    }),
  }));
}

// Search matches on login email or account name. A login-email match keeps the
// whole group; otherwise the group is kept only with the accounts that match.
function filterLoginGroups(groups: LoginGroupData[], query: string): LoginGroupData[] {
  if (isEmptyString(query)) {
    return groups;
  }

  const lowerQuery = query.toLowerCase();
  const matched: LoginGroupData[] = [];
  for (const group of groups) {
    if (group.session.email.toLowerCase().includes(lowerQuery)) {
      matched.push(group);
      continue;
    }
    const accounts = group.accounts.filter((account) => account.accountName.toLowerCase().includes(lowerQuery));
    if (accounts.length > 0) {
      matched.push({ ...group, accounts });
    }
  }
  return matched;
}

function EmptyResults() {
  return (
    // Keep the aria-controls target id even when empty, so the switch-accounts
    // button's aria-controls always points at a rendered element.
    <Box id="account-switcher-accounts-list" className={styles.workspaceList}>
      <List styleType="none" gap="0">
        <ListItem>
          <TextLabel m="200" variant="secondary">
            No results found
          </TextLabel>
        </ListItem>
      </List>
    </Box>
  );
}

type AccountRowProps = {
  account: AccountRowData;
  isExpired: boolean;
  onSelectAccount: (accountId: number) => unknown;
  onSelectLogin: (sessionId: number) => unknown;
  onSelectLoginAccount: (sessionId: number, accountId: number) => unknown;
};

// A single account beneath a login. Indented to sit under the login header.
function AccountRow({ account, isExpired, onSelectAccount, onSelectLogin, onSelectLoginAccount }: AccountRowProps) {
  const sharedProps = {
    flexGrow: '1',
    minW: '0',
    pl: '400',
    pr: '200',
    py: '150',
    textAlign: 'left',
  } as const;

  const content = (
    <Box display="flex" justifyContent="space-between" alignItems="center" gap="100" w="full">
      <Box display="flex" alignItems="center" gap="150" minW="0">
        <Avatar
          size="s"
          name={account.accountName}
          src={isEmptyString(account.logoUrl) ? undefined : account.logoUrl}
          autoColor={true}
        />
        <Truncated className={styles.workspaceItemName}>{account.accountName}</Truncated>
      </Box>
      {account.isHome ? (
        <Box flexShrink="0">
          <Label color="grey">home</Label>
        </Box>
      ) : null}
    </Box>
  );

  if (isExpired) {
    return (
      <PlainLink
        {...sharedProps}
        href="/login?addAccount=true"
        isExternal
        withSafeExternalAttributes={false}
        aria-label={`${account.accountName} account (expired)`}
        display="block"
      >
        {content}
      </PlainLink>
    );
  }

  const handleClick = () => {
    const { action } = account;
    switch (action.type) {
      case 'switch-account':
        onSelectAccount(action.accountId);
        break;
      case 'switch-login':
        onSelectLogin(action.sessionId);
        break;
      case 'switch-login-account':
        onSelectLoginAccount(action.sessionId, action.accountId);
        break;
      default: {
        const _exhaustive: never = action;
        return _exhaustive;
      }
    }
  };

  return (
    <PlainButton {...sharedProps} onClick={handleClick} aria-label={`Switch to ${account.accountName}`}>
      {content}
    </PlainButton>
  );
}

type LoginGroupProps = {
  group: LoginGroupData;
  onSelectAccount: (accountId: number) => unknown;
  onSelectLogin: (sessionId: number) => unknown;
  onSelectLoginAccount: (sessionId: number, accountId: number) => unknown;
  onLogout: (sessionId: number) => unknown;
  isLoggingOut: boolean;
};

function LoginGroup(props: LoginGroupProps) {
  const { group, onSelectAccount, onSelectLogin, onSelectLoginAccount, onLogout, isLoggingOut } = props;
  return (
    <Box as="section" className={styles.loginGroup} aria-label={`${group.session.email} account`}>
      <LoginGroupHeader
        session={group.session}
        isExpired={group.isExpired}
        onLogout={onLogout}
        isLoggingOut={isLoggingOut}
      />
      <List styleType="none" gap="0">
        {group.accounts.map((account) => (
          <li key={account.key} className={styles.sessionItem} aria-current={account.isCurrent}>
            <AccountRow
              account={account}
              isExpired={group.isExpired}
              onSelectAccount={onSelectAccount}
              onSelectLogin={onSelectLogin}
              onSelectLoginAccount={onSelectLoginAccount}
            />
          </li>
        ))}
      </List>
    </Box>
  );
}

type AccountsSwitcherBodyProps = {
  groups: LoginGroupData[];
  onSelectAccount: (accountId: number) => unknown;
  onSelectLogin: (sessionId: number) => unknown;
  onSelectLoginAccount: (sessionId: number, accountId: number) => unknown;
  onLogout: (sessionId: number) => unknown;
  loggingOutSessionId: number | undefined;
};

/**
 * The body of the "accounts" panel (page two). Each login is a group: the login
 * email heads the group with a log-out control, and the accounts that login can
 * reach are listed beneath it. Selecting an account under the current login
 * switches the active account (X-Active-Account-Id, no re-auth); selecting an
 * account under another login switches to that login. Logging out of a login uses
 * the control in its group header.
 */
function AccountsSwitcherBody(props: AccountsSwitcherBodyProps) {
  const { groups, onSelectAccount, onSelectLogin, onSelectLoginAccount, onLogout, loggingOutSessionId } = props;

  if (groups.length === 0) {
    return <EmptyResults />;
  }

  return (
    <Box id="account-switcher-accounts-list" className={styles.workspaceList}>
      {groups.map((group) => (
        <LoginGroup
          key={group.session.id}
          group={group}
          onSelectAccount={onSelectAccount}
          onSelectLogin={onSelectLogin}
          onSelectLoginAccount={onSelectLoginAccount}
          onLogout={onLogout}
          isLoggingOut={loggingOutSessionId === group.session.id}
        />
      ))}
    </Box>
  );
}

type TrackFn = ReturnType<typeof useAnalytics>['track'];

type SwitchLoginContext = {
  sessions: Session[];
  currentSession: Session | null;
  track: TrackFn;
  setDirection: (direction: 1 | -1) => void;
  setIsSwitchingAccounts: (value: boolean) => void;
};

// Switch to another login (its own session cookie), optionally landing on a
// specific account. Full reload so per-account data is refetched fresh.
function switchToLogin(ctx: SwitchLoginContext, sessionId: number, accountId?: number): void {
  const targetSession = ctx.sessions.find((s) => s.id === sessionId);
  if (targetSession == null) {
    ctx.setDirection(-1);
    ctx.setIsSwitchingAccounts(false);
    return;
  }

  void ctx.track('multiAccount', 'switchedAccounts', {
    'Session Count': ctx.sessions.length,
    'Current Account ID': ctx.currentSession?.id,
    'New Account Name': targetSession.name,
  });

  // Persist the pending switch so a post-switch failure (e.g. an expired session)
  // can surface an informative snackbar and offer re-login.
  sessionStorage.setItem(
    PENDING_SESSION_SWITCH_KEY,
    JSON.stringify({ id: targetSession.id, name: targetSession.name }),
  );
  changeToSession(sessionId);
  // changeToSession resets the active account to home, so set the chosen one after.
  if (accountId != null) {
    setActiveAccount(accountId);
  }
  ctx.setIsSwitchingAccounts(false);
  navigateExternal('/');
}

type LogoutContext = {
  sessions: Session[];
  currentSession: Session | null;
  track: TrackFn;
  logout: (sessionId: number) => Promise<unknown>;
  onError: () => void;
  setIsSwitchingAccounts: (value: boolean) => void;
};

async function logoutSession(ctx: LogoutContext, sessionId: number): Promise<void> {
  const targetSession = ctx.sessions.find((s) => s.id === sessionId);
  if (targetSession == null) {
    return;
  }

  try {
    await ctx.logout(sessionId);
  } catch {
    ctx.onError();
    return;
  }

  void ctx.track('multiAccount', 'loggedOutAccount', {
    'Session Count': ctx.sessions.length,
    'Logged-Out Account Name': targetSession.name,
    'Was Current': sessionId === ctx.currentSession?.id,
  });

  const remaining = ctx.sessions.filter((session) => session.id !== sessionId);
  updateSessions(remaining);

  if (sessionId !== ctx.currentSession?.id) {
    // Broadcast-only: peer tab(s) on that slot self-invalidate; this tab is untouched.
    authSession.invalidate(sessionId);
    return;
  }

  if (remaining.length === 0) {
    // Last account: a full /logout invalidates this slot and notifies peers.
    navigateExternal('/logout');
    return;
  }

  // Move to the next remaining session first so invalidate() targets a slot that
  // is no longer active — this tab switches instead of redirecting to login.
  changeToSession(remaining[0].id);
  authSession.invalidate(sessionId);
  ctx.setIsSwitchingAccounts(false);
  navigateExternal('/');
}

type WorkspacesListProps = {
  workspaces: MergedWorkspace[];
  currentWorkspaceId: number | undefined;
  currentWorkspaceRef: Ref<HTMLLIElement | null>;
  isSampleWorkspace: (cioWorkspaceId: number) => boolean;
  shouldReduceMotion: boolean | null;
  hasToggled: boolean;
  onWorkspaceLinkClick: (event: React.MouseEvent<HTMLAnchorElement>, options?: TargetTransitionOptions) => void;
};

function WorkspacesList(props: WorkspacesListProps) {
  return (
    <section className={styles.workspaceList}>
      <List styleType="none" gap="0">
        {props.workspaces.length > 0 ? (
          props.workspaces.map((w, index) => (
            <WorkspaceStaggerItem
              key={w.id}
              workspace={w}
              index={index}
              shouldReduceMotion={props.shouldReduceMotion}
              hasToggled={props.hasToggled}
              isSampleWorkspace={props.isSampleWorkspace(w.cio_workspace_id)}
              onWorkspaceLinkClick={props.onWorkspaceLinkClick}
              currentWorkspaceRef={w.id === props.currentWorkspaceId ? props.currentWorkspaceRef : null}
            />
          ))
        ) : (
          <ListItem>
            <TextLabel m="200" variant="secondary">
              No results found
            </TextLabel>
          </ListItem>
        )}
      </List>
    </section>
  );
}

type AccountSwitcherActions = {
  onSelectAccount: (accountId: number) => void;
  onSelectLogin: (sessionId: number) => void;
  onSelectLoginAccount: (sessionId: number, accountId: number) => void;
  onLogout: (sessionId: number) => void;
};

type UseAccountSwitcherActionsParams = {
  sessions: Session[];
  currentSession: Session | null;
  activeAccountId: number;
  memberships: AccountMembershipItem[];
  track: TrackFn;
  setDirection: (direction: 1 | -1) => void;
  setIsSwitchingAccounts: (value: boolean) => void;
  logout: (sessionId: number) => Promise<unknown>;
  onLogoutError: () => void;
};

// The switcher's row handlers, kept out of the component so each stays small and
// focused. Selecting an account switches the active account; selecting a login
// switches identity; the two combine when picking an account under another login.
function useAccountSwitcherActions(params: UseAccountSwitcherActionsParams): AccountSwitcherActions {
  const { sessions, currentSession, activeAccountId, memberships, track, setDirection, setIsSwitchingAccounts } =
    params;
  const switchLoginContext: SwitchLoginContext = {
    sessions,
    currentSession,
    track,
    setDirection,
    setIsSwitchingAccounts,
  };

  const onSelectAccount = (accountId: number) => {
    if (accountId === activeAccountId) {
      setDirection(-1);
      setIsSwitchingAccounts(false);
      return;
    }
    const target = memberships.find((membership) => membership.account_id === accountId);
    void track('multiAccount', 'switchedAccounts', {
      'Session Count': memberships.length,
      'Current Account ID': activeAccountId,
      'New Account Name': target?.account_name ?? '',
    });
    switchAccount(accountId);
  };

  const onSelectLogin = (sessionId: number) => {
    if (sessionId === currentSession?.id) {
      setDirection(-1);
      setIsSwitchingAccounts(false);
      return;
    }
    switchToLogin(switchLoginContext, sessionId);
  };

  const onSelectLoginAccount = (sessionId: number, accountId: number) => {
    if (sessionId === currentSession?.id) {
      onSelectAccount(accountId);
      return;
    }
    switchToLogin(switchLoginContext, sessionId, accountId);
  };

  const onLogout = (sessionId: number) => {
    void logoutSession(
      { sessions, currentSession, track, logout: params.logout, onError: params.onLogoutError, setIsSwitchingAccounts },
      sessionId,
    );
  };

  return { onSelectAccount, onSelectLogin, onSelectLoginAccount, onLogout };
}

type SwitcherSearchFieldProps = {
  isSwitchingAccounts: boolean;
  shouldShowSearch: boolean;
  searchQuery: string;
  onSearch: (value: string) => void;
};

function SwitcherSearchField({
  isSwitchingAccounts,
  shouldShowSearch,
  searchQuery,
  onSearch,
}: SwitcherSearchFieldProps) {
  const label = isSwitchingAccounts ? 'Accounts' : 'Workspaces';
  return (
    <Box px="200" py="200">
      {shouldShowSearch ? (
        <Search
          value={searchQuery}
          onSearchInput={onSearch}
          onSearch={onSearch}
          shouldSearchOnClear={true}
          shouldSearchOnInput={true}
          placeholder={isSwitchingAccounts ? 'Find an account…' : 'Find a workspace…'}
          autoFocus={true}
          label={label}
          w="full"
          maxW="full"
        />
      ) : (
        <TextLabel>{label}</TextLabel>
      )}
    </Box>
  );
}

type AccountsPanelBodyProps = {
  groups: LoginGroupData[];
  actions: AccountSwitcherActions;
  loggingOutSessionId: number | undefined;
  canAddAccount: boolean;
  onAddAccount: () => void;
};

function AccountsPanelBody({
  groups,
  actions,
  loggingOutSessionId,
  canAddAccount,
  onAddAccount,
}: AccountsPanelBodyProps) {
  return (
    <>
      <AccountsSwitcherBody
        groups={groups}
        onSelectAccount={actions.onSelectAccount}
        onSelectLogin={actions.onSelectLogin}
        onSelectLoginAccount={actions.onSelectLoginAccount}
        onLogout={actions.onLogout}
        loggingOutSessionId={loggingOutSessionId}
      />
      <Box as="section" display="flex" alignItems="center" py="150" px="200">
        <Button
          flexGrow="1"
          isDisabled={!canAddAccount}
          // Navigating internally to the login page breaks form-field tracking, so
          // this is a full reload for now.
          href="/login?addAccount=true"
          isExternal={true}
          withSafeExternalAttributes={false}
          onClick={onAddAccount}
        >
          Add another account
        </Button>
      </Box>
    </>
  );
}

type WorkspacesPanelBodyProps = WorkspacesListProps;

function WorkspacesPanelBody(props: WorkspacesPanelBodyProps) {
  return (
    <>
      <WorkspacesList {...props} />
      <Box as="section" display="flex" alignItems="center" py="150" px="200">
        <Button
          href="/settings/workspaces"
          flexGrow="1"
          onClick={(event) => props.onWorkspaceLinkClick(event as React.MouseEvent<HTMLAnchorElement>)}
        >
          Manage all workspaces
        </Button>
      </Box>
    </>
  );
}

export function WorkspaceDropdown(props: {
  onWorkspaceLinkClick: (event: React.MouseEvent<HTMLAnchorElement>, options?: TargetTransitionOptions) => void;
}) {
  const auth = useAuth();

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [direction, setDirection] = useState<1 | -1>(1);
  const [hasToggled, setHasToggled] = useState(false);
  const shouldReduceMotion = useReducedMotion();
  const workspaceCountQuery = useWorkspaceCount();
  const { isSampleWorkspace } = useSampleWorkspace();
  const filteredWorkspacesQuery = useFilterWorkspacesByName(searchQuery, {
    includeSampleWorkspace: true,
  });

  const { isSwitchingAccounts, setIsSwitchingAccounts } = use(AccountSwitcherContext);
  const currentWorkspace = useCurrentWorkspace();

  const canSwitchAccounts = !auth.account.is_masq;
  const sessions = useStore(sessionsStore);
  const currentSession = useStore(currentSessionStore);
  const snackbars = useSnackbars();

  // Accounts the current login belongs to, switched via the X-Active-Account-Id
  // header (same session cookie) rather than by login.
  const membershipsQuery = useAccountMemberships();
  const memberships = membershipsQuery.data ?? [];
  // The account list is gated on the decouple_users flag — the same per-user flag
  // the backend uses to honor X-Active-Account-Id. Without it, switching accounts
  // would be a no-op, so the list must not appear. Membership count alone is not a
  // safe gate: a backfilled login can hold multiple rows while the flag is off.
  const decoupleEnabled = Boolean(auth.account.feature_flags?.[DECOUPLE_USERS_FLAG]);
  // Expand the current login into its accounts once the flag is on and there are
  // loaded memberships. Any membership is enough (buildAccountRows injects the home
  // account, so even a lone non-home membership stays reachable). Gating on the
  // count (not isSuccess) keeps the cached list showing through a transient refetch
  // error — TanStack retains the last data while status flips to error.
  const membershipSwitchingEnabled = canSwitchAccounts && decoupleEnabled && memberships.length >= 1;
  const activeAccountId = auth.account.id;

  // Accounts grouped beneath the login that reaches them (page two).
  const currentLoginDecoupled = canSwitchAccounts && decoupleEnabled;
  // The authenticated user's home account — a home-id fallback for the current
  // login when its session metadata predates home_account_id.
  const currentUserAccountId = auth.user?.account_id;
  const loginGroups = useMemo(
    () =>
      buildLoginGroups({
        sessions,
        currentSession,
        currentLoginDecoupled,
        currentUserAccountId,
        memberships,
        membershipSwitchingEnabled,
        activeAccountId,
      }),
    [
      sessions,
      currentSession,
      currentLoginDecoupled,
      currentUserAccountId,
      memberships,
      membershipSwitchingEnabled,
      activeAccountId,
    ],
  );
  const visibleLoginGroups = useMemo(() => filterLoginGroups(loginGroups, searchQuery), [loginGroups, searchQuery]);
  const totalAccountRows = useMemo(
    () => loginGroups.reduce((count, group) => count + group.accounts.length, 0),
    [loginGroups],
  );

  const allWorkspacesCount = workspaceCountQuery.data ?? 0;
  const workspaceData = filteredWorkspacesQuery.data ?? [];

  const shouldShowSearch = isSwitchingAccounts ? totalAccountRows > 3 : allWorkspacesCount > 3;

  const currentWorkspaceElementRef = useRef<HTMLLIElement | null>(null);

  useMountEffect(() => {
    if (currentWorkspaceElementRef.current == null) {
      return;
    }

    currentWorkspaceElementRef.current.scrollIntoView({
      block: 'center',
    });
  });

  const { track } = useAnalytics();

  const handleToggleAccounts = () => {
    setHasToggled(true);
    setDirection(isSwitchingAccounts ? -1 : 1);
    if (!isSwitchingAccounts) {
      void track('multiAccount', 'viewedAccounts', { 'Session Count': sessions.length });
    }
    setIsSwitchingAccounts(!isSwitchingAccounts);
    setSearchQuery('');
  };

  const logoutSessionMutation = useHydraUIAPIMutation((variables: { id: number }) =>
    hydraUiApi.sessions.logout({ params: { path: { id: String(variables.id) } } }),
  );

  const actions = useAccountSwitcherActions({
    sessions,
    currentSession,
    activeAccountId,
    memberships,
    track,
    setDirection,
    setIsSwitchingAccounts,
    logout: (id) => logoutSessionMutation.mutateAsync({ id }),
    onLogoutError: () =>
      snackbars.create({
        id: 'multi-account.account.logout.error',
        message: "Couldn't log out of that account. Please try again",
        isError: true,
      }),
  });

  return (
    <PopoverContent className={styles.content}>
      {canSwitchAccounts ? (
        <SwitchAccountButton
          onClick={handleToggleAccounts}
          // When the current login is decoupled the active account can differ
          // from the login's home, so show the resolved active-account name (not
          // just when the /v1/memberships fallback happens to be driving the list).
          accountName={currentLoginDecoupled ? auth.account.name : undefined}
          accountLogoUrl={auth.account.logo_url}
        />
      ) : null}
      <SwitcherSearchField
        isSwitchingAccounts={isSwitchingAccounts}
        shouldShowSearch={shouldShowSearch}
        searchQuery={searchQuery}
        onSearch={setSearchQuery}
      />
      <m.div className={styles.panelWrapper}>
        <AnimatePresence mode="wait" custom={direction} initial={false}>
          {isSwitchingAccounts ? (
            <m.section
              key="accounts"
              custom={direction}
              variants={panelVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              <AccountsPanelBody
                groups={visibleLoginGroups}
                actions={actions}
                loggingOutSessionId={logoutSessionMutation.isPending ? logoutSessionMutation.variables?.id : undefined}
                canAddAccount={sessions.length < MAX_ACCOUNTS}
                onAddAccount={() =>
                  void track('multiAccount', 'clickedAddAccount', { 'Session Count': sessions.length })
                }
              />
            </m.section>
          ) : (
            <m.div
              key="workspaces"
              custom={direction}
              variants={panelVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              <WorkspacesPanelBody
                workspaces={workspaceData}
                currentWorkspaceId={currentWorkspace?.id}
                currentWorkspaceRef={currentWorkspaceElementRef}
                isSampleWorkspace={isSampleWorkspace}
                shouldReduceMotion={shouldReduceMotion}
                hasToggled={hasToggled}
                onWorkspaceLinkClick={props.onWorkspaceLinkClick}
              />
            </m.div>
          )}
        </AnimatePresence>
      </m.div>
    </PopoverContent>
  );
}
